import React, { useState, useEffect } from "react";
import { useForm, type Resolver, type FieldErrors } from "react-hook-form";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Input } from "@/components/Input";
import { Select, SelectOption } from "@/components/Select";
import { Textarea } from "@/components/Textarea";
import { Switch } from "@/components/Switch";
import { useToast } from "@/context/ToastContext";
import { clientApi } from "@/lib/api";
import {
    ClientItem,
    ClientFormData,
    CountryItem,
    ProvinceItem,
} from "@/pages/Clients/types";
import { ClientType } from "@/pages/Tenders/types";
import { apiFieldErrors } from "@/utils/apiError";

export interface ClientModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSaved: (client: ClientItem) => void;
    client?: ClientItem | null;
    clientTypes: ClientType[];
    existingClients?: ClientItem[];
}

export const ClientModal: React.FC<ClientModalProps> = ({
    isOpen,
    onClose,
    onSaved,
    client = null,
    clientTypes = [],
    existingClients = [],
}) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();

    const schema = z
        .object({
            name: z
                .string()
                .trim()
                .min(1, t("common.required"))
                .max(255, t("workspace.form.too_long")),
            email: z.union([
                z.literal(""),
                z.email(t("workspace.form.invalid_email")),
            ]),
        })
        .passthrough();
    const resolver: Resolver<ClientFormData> = async (values) => {
        const parsed = schema.safeParse(values);
        return parsed.success
            ? { values, errors: {} }
            : {
                  values: {},
                  errors: Object.fromEntries(
                      parsed.error.issues.map((issue) => [
                          issue.path[0],
                          { type: issue.code, message: issue.message },
                      ])
                  ) as FieldErrors<ClientFormData>,
              };
    };
    const form = useForm<ClientFormData>({
        resolver,
        defaultValues: {
            name: "",
            client_type_id: null,
            tax_id: "",
            phone: "",
            email: "",
            address: "",
            city: "",
            province_id: null,
            postal_code: "",
            country_id: null,
            notes: "",
            is_active: true,
        },
    });
    const formData = form.watch();
    const setFormData = (values: ClientFormData): void => form.reset(values);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [countries, setCountries] = useState<CountryItem[]>([]);
    const [provinces, setProvinces] = useState<ProvinceItem[]>([]);

    useEffect(() => {
        if (isOpen) {
            clientApi.countries().then((res) => setCountries(res?.data || []));
            clientApi.provinces().then((res) => setProvinces(res?.data || []));
        }
    }, [isOpen]);

    useEffect(() => {
        if (client) {
            setFormData({
                name: client.name || "",
                client_type_id: client.client_type_id ?? null,
                tax_id: client.tax_id || "",
                phone: client.phone || "",
                email: client.email || "",
                address: client.address || "",
                city: client.city || "",
                province_id: client.province_id ?? null,
                postal_code: client.postal_code || "",
                country_id: client.country_id ?? null,
                notes: client.notes || "",
                is_active: client.is_active ?? true,
            });
        } else {
            setFormData({
                name: "",
                client_type_id: null,
                tax_id: "",
                phone: "",
                email: "",
                address: "",
                city: "",
                province_id: null,
                postal_code: "",
                country_id: null,
                notes: "",
                is_active: true,
            });
        }
    }, [client, isOpen]);

    const handleChange = (
        field: keyof ClientFormData,
        value: string | number | boolean | null
    ): void => {
        form.setValue(field, value, {
            shouldDirty: true,
            shouldValidate: true,
        });
    };

    const handleSubmit = async (): Promise<void> => {
        if (!(await form.trigger())) {
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                name: formData.name.trim(),
                client_type_id: formData.client_type_id
                    ? Number(formData.client_type_id)
                    : null,
                tax_id: formData.tax_id.trim() || null,
                phone: formData.phone.trim() || null,
                email: formData.email.trim() || null,
                address: formData.address.trim() || null,
                city: formData.city.trim() || null,
                province_id: formData.province_id
                    ? Number(formData.province_id)
                    : null,
                postal_code: formData.postal_code.trim() || null,
                country_id: formData.country_id
                    ? Number(formData.country_id)
                    : null,
                notes: formData.notes.trim() || null,
                is_active: Boolean(formData.is_active),
            };

            let res;
            if (client?.id) {
                res = await clientApi.update(client.id, payload);
            } else {
                res = await clientApi.create(payload);
            }

            if (res?.data) {
                const savedMessage = client?.id
                    ? t("clients.saved_success")
                    : t("clients.created_success");
                showSuccess(savedMessage);
                onSaved(res.data);
                onClose();
            }
        } catch (err: unknown) {
            for (const [field, message] of Object.entries(
                apiFieldErrors(err)
            )) {
                if (field in formData) {
                    form.setError(field as keyof ClientFormData, {
                        type: "server",
                        message,
                    });
                }
            }
            showError(err instanceof Error ? err.message : t("common.error"));
        } finally {
            setIsSubmitting(false);
        }
    };

    const typeOptions: SelectOption<number>[] = clientTypes.map((ct) => ({
        value: ct.id,
        label: ct.name,
    }));

    const countryOptions: SelectOption<number>[] = countries.map((c) => ({
        value: c.id,
        label: c.name,
    }));

    const provinceOptions: SelectOption<number>[] = provinces.map((p) => ({
        value: p.id,
        label: p.name,
    }));

    const isSpain =
        formData.country_id &&
        countries.find((c) => c.id === formData.country_id)?.is_spain;
    const clientNames = React.useMemo(() => {
        if (!client?.id) return existingClients.map((c) => c.name);
        return existingClients
            .filter((c) => c.id !== client.id)
            .map((c) => c.name);
    }, [existingClients, client?.id]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            hasUnsavedChanges={form.formState.isDirty}
            title={
                client?.id ? t("clients.edit_client") : t("clients.new_client")
            }
            maxWidth="2xl"
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            submitLabel={t("common.save")}
        >
            <div className="space-y-4">
                <Switch
                    checked={formData.is_active}
                    onChange={(checked: boolean) =>
                        handleChange("is_active", checked)
                    }
                    label={t("clients.is_active")}
                    placeholder={t("clients.active_desc")}
                />

                <Input
                    label={t("clients.name")}
                    value={formData.name}
                    error={form.formState.errors.name?.message}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange("name", e.target.value)
                    }
                    placeholder={t("clients.name_placeholder")}
                    required
                    autoFocus
                    similars={clientNames}
                />

                <Select<number>
                    label={t("clients.client_type")}
                    value={formData.client_type_id ?? 0}
                    error={form.formState.errors.client_type_id?.message}
                    onChange={(val: number | string) =>
                        handleChange("client_type_id", val ? Number(val) : null)
                    }
                    options={typeOptions}
                    placeholder={t("clients.select_type")}
                    clearable
                    full
                />

                <Input
                    label={t("clients.tax_id")}
                    value={formData.tax_id}
                    error={form.formState.errors.tax_id?.message}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange("tax_id", e.target.value)
                    }
                    placeholder={t("clients.tax_id_placeholder")}
                />

                <Input
                    label={t("clients.phone")}
                    value={formData.phone}
                    error={form.formState.errors.phone?.message}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange("phone", e.target.value)
                    }
                    placeholder={t("clients.phone_placeholder")}
                />

                <Input
                    label={t("clients.email")}
                    type="email"
                    value={formData.email}
                    error={form.formState.errors.email?.message}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange("email", e.target.value)
                    }
                    placeholder={t("clients.email_placeholder")}
                />

                <Input
                    label={t("clients.address")}
                    value={formData.address}
                    error={form.formState.errors.address?.message}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange("address", e.target.value)
                    }
                    placeholder={t("clients.address_placeholder")}
                />

                <Input
                    label={t("clients.city")}
                    value={formData.city}
                    error={form.formState.errors.city?.message}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange("city", e.target.value)
                    }
                    placeholder={t("clients.city_placeholder")}
                />

                <Input
                    label={t("clients.postal_code")}
                    value={formData.postal_code}
                    error={form.formState.errors.postal_code?.message}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange("postal_code", e.target.value)
                    }
                    placeholder={t("clients.postal_code_placeholder")}
                />

                <Select<number>
                    label={t("clients.country")}
                    value={formData.country_id ?? 0}
                    error={form.formState.errors.country_id?.message}
                    onChange={(val: number | string) =>
                        handleChange("country_id", val ? Number(val) : null)
                    }
                    options={countryOptions}
                    placeholder={t("clients.select_country")}
                    searchable
                    clearable
                    full
                />

                {isSpain && (
                    <Select<number>
                        label={t("clients.province")}
                        value={formData.province_id ?? 0}
                        error={form.formState.errors.province_id?.message}
                        onChange={(val: number | string) =>
                            handleChange(
                                "province_id",
                                val ? Number(val) : null
                            )
                        }
                        options={provinceOptions}
                        placeholder={t("clients.select_province")}
                        searchable
                        clearable
                        full
                    />
                )}

                <Textarea
                    label={t("clients.notes")}
                    value={formData.notes}
                    error={form.formState.errors.notes?.message}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        handleChange("notes", e.target.value)
                    }
                    placeholder={t("clients.notes_placeholder")}
                    rows={3}
                    fullscreenEnabled={true}
                    resizable={true}
                />
            </div>
        </Modal>
    );
};
